import { useCallback, useEffect, useRef, useState, type DragEvent } from "react";
import { toast } from "sonner";
import { Paperclip } from "lucide-react";
import { conversations as conversationsApi, type ConversationMessage } from "@/features/conversations";
import { underwriting, processorEmails, type UnderwritingThread } from "@/features/applications/api/applications.api";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Markdown } from "@/components/Markdown";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/features/auth/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Notice } from "@/components/Notice";
import { Loading } from "@/components/Loading";
import { toastApiError } from "@/lib/toast";
import { formatDateTime } from "@/utils/dates";
import { cn } from "@/lib/utils";
import { SectionCard } from "../primitives";
import { bodyKey, htmlToThreadText, parseEmailThread, type ThreadEmail } from "./emailThread";
import { UnderwritingChecklist } from "./UnderwritingChecklist";
import { checklistFor } from "./checklistItems";

const UNDERWRITING = "Underwriting";
const TEAM = "BLogic Onboarding Team";

const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer.types).includes("Files");

export function UnderwritingPanel({ appId, agentIds, isAgent, business, note }: { appId: string; agentIds: (string | null | undefined)[]; isAgent: boolean; business?: string; note?: string | null }) {
  const { user } = useAuth();
  const [thread, setThread] = useState<UnderwritingThread | null>(null);
  const [messages, setMessages] = useState<ConversationMessage[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const [sending, setSending] = useState(false);
  const [editingTo, setEditingTo] = useState(false);
  const [toDraft, setToDraft] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const [logOpen, setLogOpen] = useState(false);
  const [fullEmail, setFullEmail] = useState<{ subject: string | null; from: string | null; receivedAt: string; markdown: string } | null>(null);

  async function openFull(emailId: string) {
    try {
      setFullEmail(await processorEmails.full(emailId));
    } catch (e) {
      toastApiError(e, "Couldn't open the full email.");
    }
  }

  const load = useCallback(async () => {
    try {
      const t = await underwriting.get(appId);
      const page = await conversationsApi.listMessages(t.conversationId, { limit: 100 });
      setThread(t);
      const when = (m: ConversationMessage) => {
        const meta = t.meta[m.id];
        return Date.parse(meta?.source === "logged" ? meta.at : m.createdAt) || 0;
      };
      setMessages([...page.items].filter((m) => !m.isDeleted).sort((a, b) => when(a) - when(b) || a.seq - b.seq));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't load the underwriting chat.");
    }
  }, [appId]);

  useEffect(() => { void load(); }, [load]);

  async function deliver(messageId: string) {
    try {
      await underwriting.deliver(appId, messageId);
    } catch (e) {
      toastApiError(e, "Saved in the chat, but the email to underwriting didn't go out.");
    }
  }

  async function post(body: string, attached: File[]) {
    if (!thread) return false;
    setSending(true);
    try {
      const input = { body, bodyFormat: "Markdown" as const };
      const msg = attached.length
        ? await conversationsApi.postMessageWithFiles(thread.conversationId, input, attached)
        : await conversationsApi.postMessage(thread.conversationId, input);
      await deliver(msg.id);
      await load();
      return true;
    } catch (e) {
      toastApiError(e, "Couldn't send your reply.");
      return false;
    } finally {
      setSending(false);
    }
  }

  async function send() {
    if (!draft.trim() && files.length === 0) return;
    if (await post(draft.trim() || "(see attached)", files)) {
      setDraft("");
      setFiles([]);
    }
  }

  async function saveRecipient() {
    try {
      await underwriting.setRecipient(appId, toDraft.trim());
      setEditingTo(false);
      toast.success("Underwriter email updated.");
      await load();
    } catch (e) {
      toastApiError(e, "Couldn't update the underwriter email.");
    }
  }

  if (error) return <Notice tone="error">{error}</Notice>;
  if (!thread || !messages) return <Loading>Loading underwriting chat…</Loading>;

  const nameFor = (m: ConversationMessage) => {
    const meta = thread.meta[m.id];
    if (meta?.source === "logged") {
      if (meta.fromUnderwriting) return UNDERWRITING;
      return meta.senderName ? `${meta.senderName} · ${TEAM}` : TEAM;
    }
    if (m.authorName === UNDERWRITING && !m.authorId) return UNDERWRITING;
    if (m.authorId && agentIds.includes(m.authorId)) return m.authorName ?? "Agent";
    return m.authorName ? `${m.authorName} · ${TEAM}` : TEAM;
  };
  const metaLine = (m: ConversationMessage) => {
    const meta = thread.meta[m.id];
    if (meta?.source === "logged") {
      const who = meta.fromUnderwriting && !isAgent ? [meta.senderName, meta.email].filter(Boolean).join(" ") : null;
      return `Logged from Outlook${who ? ` · from ${who}` : ""} · sent ${formatDateTime(meta.at)}`;
    }
    if (isAgent) return !meta && m.authorId === user?.userId ? "Not emailed yet" : null;
    if (!meta) return m.authorId ? "Not emailed yet" : null;
    if (meta.source === "sending") return "Sending…";
    if (meta.source === "processor-email") return "From the processor's status email · posted automatically";
    if (meta.source === "received") return `Received by email${meta.email ? ` from ${meta.email}` : ""} · posted automatically`;
    return `Delivered by email to ${meta.email} · ${meta.subject}`;
  };

  const lastMessage = messages[messages.length - 1];
  const awaitingReply = lastMessage !== undefined && nameFor(lastMessage) === UNDERWRITING;
  const checklist = thread.open && thread.to && awaitingReply ? checklistFor(lastMessage.body) : [];
  const squash = (t: string) => t.replace(/\s+/g, " ").trim().toLowerCase();
  const noteInChat = !note || messages.some((m) => squash(m.body).includes(squash(note)));

  return (
    <SectionCard title={thread.open ? "Underwriting" : "Underwriting · closed"}>
      {!isAgent && (
        <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
          {editingTo ? (
            <div className="flex flex-wrap items-center gap-2">
              <Input value={toDraft} onChange={(e) => setToDraft(e.target.value)} className="max-w-xs" aria-label="Underwriter email" />
              <Button type="button" size="sm" onClick={saveRecipient}>Save</Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setEditingTo(false)}>Cancel</Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>To <span className="font-medium">{thread.to ?? "no underwriter email yet"}</span></span>
              {thread.cc && <span>Cc <span className="font-medium">{thread.cc}</span></span>}
              <span className="text-slate-400">·</span>
              <span>Subject <span className="font-medium">{thread.subject}</span></span>
              <button type="button" className="text-xs text-slate-600 underline" onClick={() => { setToDraft(thread.to ?? ""); setEditingTo(true); }}>
                Change
              </button>
            </div>
          )}
        </div>
      )}

      {!noteInChat && (
        <p className="mb-4 border-l-2 border-amber-400 pl-3 text-sm text-slate-700">
          <span className="font-medium">Underwriting needs:</span> {note}
        </p>
      )}

      {messages.length === 0 ? (
        <p className="text-sm text-slate-500">No messages yet. When underwriting asks for items, their request shows up here.</p>
      ) : (
        <ol className="flex flex-col gap-3">
          {messages.map((m) => {
            const fromUw = nameFor(m) === UNDERWRITING;
            const line = metaLine(m);
            return (
              <li key={m.id} className={cn("flex max-w-[80%] flex-col", fromUw ? "self-start" : "self-end items-end")}>
                <span className="mb-1 text-xs font-semibold text-slate-700">{nameFor(m)}</span>
                <div className={cn(
                  "rounded-2xl border px-3 py-2 text-sm",
                  fromUw ? "rounded-bl-sm border-slate-200 bg-slate-50" : "rounded-br-sm border-blue-200 bg-blue-50",
                )}>
                  {m.bodyFormat === "Markdown" ? <Markdown source={m.body} /> : <p className="whitespace-pre-wrap">{m.body}</p>}
                  {m.attachments.length > 0 && (
                    <div className="mt-2 space-y-1 text-xs">
                      {m.attachments.map((a) => (
                        <div key={a.id}>📎 {a.url ? <a href={a.url} target="_blank" rel="noreferrer" className="underline">{a.fileName}</a> : a.fileName}</div>
                      ))}
                    </div>
                  )}
                </div>
                <span className="mt-1 text-[11px] text-slate-500">
                  {thread.meta[m.id]?.source === "logged" ? line : <>{formatDateTime(m.createdAt)}{line && ` · ${line}`}</>}
                  {!isAgent && thread.meta[m.id]?.emailId && (
                    <button type="button" className="ml-2 underline" onClick={() => openFull(thread.meta[m.id]!.emailId!)}>View full email</button>
                  )}
                  {line === "Not emailed yet" && m.authorId === user?.userId && thread.to && (
                    <button type="button" className="ml-2 underline" onClick={async () => { await deliver(m.id); await load(); }}>Send email</button>
                  )}
                </span>
              </li>
            );
          })}
        </ol>
      )}

      {checklist.length > 0 && (
        <UnderwritingChecklist
          key={lastMessage!.id}
          business={business?.trim() || "this application"}
          items={checklist}
          sending={sending}
          onSend={post}
        />
      )}

      {thread.open && isAgent && !thread.to ? (
        <p className="mt-4 text-xs text-slate-500">
          You can reply here once back office adds the underwriter's email.
        </p>
      ) : thread.open ? (
        <div
          className={`relative mt-4 space-y-2 rounded-md border-t border-slate-100 pt-3 ${dragging ? "outline-dashed outline-2 outline-offset-4 outline-primary" : ""}`}
          onDragEnter={(e) => { if (hasFiles(e)) { e.preventDefault(); setDragging(true); } }}
          onDragOver={(e) => { if (hasFiles(e)) { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; } }}
          onDragLeave={(e) => { if (!(e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget))) setDragging(false); }}
          onDrop={(e) => {
            if (!hasFiles(e)) return;
            e.preventDefault();
            setDragging(false);
            const dropped = Array.from(e.dataTransfer.files).filter((f) => f.size > 0 || f.type !== "");
            if (dropped.length) setFiles((prev) => [...prev, ...dropped]);
          }}
        >
          {dragging && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-md bg-primary/10 text-sm font-medium text-slate-800">
              Drop files to attach
            </div>
          )}
          <RichTextEditor value={draft} onChange={setDraft} placeholder={checklist.length > 0 ? "Or write your own reply…" : "Reply to underwriting…"} />
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
              <Paperclip className="mr-1 h-4 w-4" /> Attach
            </Button>
            {files.length === 0 && <span className="text-xs text-slate-400">or drag files here</span>}
            <input ref={fileRef} type="file" multiple hidden onChange={(e) => { setFiles([...files, ...Array.from(e.target.files ?? [])]); e.target.value = ""; }} />
            {files.map((f, i) => (
              <span key={i} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">
                {f.name} <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles(files.filter((_, j) => j !== i))}>×</button>
              </span>
            ))}
            <span className="flex-1" />
            <Button type="button" onClick={send} disabled={sending || (!draft.trim() && files.length === 0)}>
              {sending ? "Sending…" : "Send"}
            </Button>
          </div>
          {!isAgent && (
            <p className="text-xs text-slate-500">
              Sends instantly from boarding@blogicsystems.com in Outlook, signed "Thank you, {user?.name ?? TEAM}". The agent sees it here; they aren't copied on the email.
            </p>
          )}
        </div>
      ) : (
        <p className="mt-4 text-xs text-slate-500">
          This conversation is closed. It opens again if underwriting asks for more items.
        </p>
      )}
      {!isAgent && (
        <div className="mt-3 flex justify-end">
          <Button type="button" size="sm" variant="outline" onClick={() => setLogOpen(true)}>Import email thread</Button>
        </div>
      )}
      <ImportThreadDialog
        open={logOpen}
        existing={new Set(messages.map((m) => bodyKey(m.body)))}
        onClose={() => setLogOpen(false)}
        onImportOne={async (e) => {
          await underwriting.log(appId, {
            fromUnderwriting: e.fromUnderwriting,
            senderName: e.senderName,
            senderEmail: e.senderEmail,
            sentAt: (e.sentAt ?? new Date()).toISOString(),
            text: e.body,
          }, []);
        }}
        onFinished={() => load()}
      />
      <Dialog open={fullEmail !== null} onOpenChange={(o) => { if (!o) setFullEmail(null); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{fullEmail?.subject ?? "Full email"}</DialogTitle>
            <DialogDescription>
              {fullEmail?.from}{fullEmail && ` · ${formatDateTime(fullEmail.receivedAt)}`}
            </DialogDescription>
          </DialogHeader>
          {fullEmail && <Markdown source={fullEmail.markdown} />}
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}

function toLocalInput(d: Date | null) {
  if (!d) return "";
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function ImportThreadDialog({ open, existing, onClose, onImportOne, onFinished }: {
  open: boolean;
  existing: Set<string>;
  onClose: () => void;
  onImportOne: (email: ThreadEmail) => Promise<void>;
  onFinished: () => void;
}) {
  const [emails, setEmails] = useState<ThreadEmail[] | null>(null);
  const [skip, setSkip] = useState<Set<number>>(new Set());
  const [pasted, setPasted] = useState("");
  const [dragging, setDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const reset = () => { setEmails(null); setSkip(new Set()); setPasted(""); };
  const close = () => { reset(); onClose(); };

  function load(text: string, fileDate: Date | null) {
    const parsed = parseEmailThread(text).map((e) => (e.headerless ? { ...e, sentAt: e.sentAt ?? fileDate ?? new Date() } : e));
    if (parsed.length === 0) {
      toast.error("Couldn't find any emails in that. Try exporting the thread again.");
      return;
    }
    setEmails(parsed);
    setSkip(new Set(parsed.map((e, i) => (existing.has(bodyKey(e.body)) ? i : -1)).filter((i) => i >= 0)));
  }

  async function readFile(file: File) {
    const raw = await file.text();
    const text = /\.html?$/i.test(file.name) || /<html|<div|<p[ >]/i.test(raw.slice(0, 5000)) ? htmlToThreadText(raw) : raw;
    load(text, file.lastModified ? new Date(file.lastModified) : null);
  }

  const update = (i: number, patch: Partial<ThreadEmail>) =>
    setEmails((prev) => prev && prev.map((e, j) => (j === i ? { ...e, ...patch } : e)));

  const chosen = emails?.filter((_, i) => !skip.has(i)) ?? [];

  async function run() {
    if (!emails || chosen.length === 0) return;
    setImporting(true);
    let done = 0;
    try {
      for (let i = 0; i < emails.length; i++) {
        if (skip.has(i)) continue;
        await onImportOne(emails[i]);
        done++;
        setSkip((prev) => new Set(prev).add(i));
      }
      toast.success(`Imported ${done} email${done === 1 ? "" : "s"}. Nothing was sent.`);
      close();
    } catch (e) {
      toastApiError(e, done > 0
        ? `Imported ${done}, then one failed. The imported ones are unticked. Click Import to retry the rest.`
        : "Couldn't import the emails. Try again.");
    } finally {
      setImporting(false);
      if (done > 0) onFinished();
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import email thread</DialogTitle>
          <DialogDescription>Adds the emails to this chat with their senders and dates. Nothing is sent, and the agent isn't notified.</DialogDescription>
        </DialogHeader>
        {!emails ? (
          <div className="space-y-3">
            <div
              className={cn("flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-4 py-8 text-sm", dragging ? "border-primary bg-primary/10" : "border-slate-300")}
              onDragOver={(e) => { if (hasFiles(e)) { e.preventDefault(); setDragging(true); } }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { if (!hasFiles(e)) return; e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) void readFile(f); }}
            >
              <span>Drop the saved email (.html) here</span>
              <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()}>Choose file</Button>
              <input ref={fileRef} type="file" accept=".html,.htm,.txt,.eml" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void readFile(f); e.target.value = ""; }} />
            </div>
            <Textarea rows={5} value={pasted} onChange={(e) => setPasted(e.target.value)} placeholder="…or paste the whole email thread here" />
            <div className="flex justify-end">
              <Button type="button" onClick={() => load(pasted, null)} disabled={!pasted.trim()}>Read thread</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Found {emails.length} email{emails.length === 1 ? "" : "s"}, oldest first. Untick any you don't want.</p>
            <ol className="space-y-2">
              {emails.map((e, i) => (
                <li key={i} className={cn("rounded-md border px-3 py-2 text-sm", skip.has(i) ? "opacity-50" : "")}>
                  <div className="flex flex-wrap items-center gap-2">
                    <input type="checkbox" aria-label={`Import email ${i + 1}`} checked={!skip.has(i)} onChange={() => setSkip((prev) => { const n = new Set(prev); if (n.has(i)) n.delete(i); else n.add(i); return n; })} />
                    {e.headerless ? (
                      <>
                        <Button type="button" size="sm" variant={e.fromUnderwriting ? "outline" : "default"} onClick={() => update(i, { fromUnderwriting: false })}>BLogic team</Button>
                        <Button type="button" size="sm" variant={e.fromUnderwriting ? "default" : "outline"} onClick={() => update(i, { fromUnderwriting: true })}>Underwriting</Button>
                        <Input type="datetime-local" className="h-8 w-auto" aria-label="When this email was sent" value={toLocalInput(e.sentAt)} onChange={(ev) => update(i, { sentAt: ev.target.value ? new Date(ev.target.value) : null })} />
                      </>
                    ) : (
                      <>
                        <span className="font-medium">{e.fromUnderwriting ? "Underwriting" : "BLogic"} · {e.senderName ?? e.senderEmail ?? "Unknown"}</span>
                        {e.sentAt ? (
                          <span className="text-xs text-slate-500">{formatDateTime(e.sentAt.toISOString())}</span>
                        ) : (
                          <Input type="datetime-local" className="h-8 w-auto" aria-label="When this email was sent" value="" onChange={(ev) => update(i, { sentAt: ev.target.value ? new Date(ev.target.value) : null })} />
                        )}
                      </>
                    )}
                    {existing.has(bodyKey(e.body)) && <span className="text-xs text-slate-500">Already in the chat</span>}
                  </div>
                  <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-slate-600">{e.body}</p>
                </li>
              ))}
            </ol>
            <div className="flex justify-between gap-2">
              <Button type="button" variant="outline" onClick={reset}>Start over</Button>
              <Button type="button" onClick={run} disabled={importing || chosen.length === 0}>{importing ? "Importing…" : `Import ${chosen.length} email${chosen.length === 1 ? "" : "s"}`}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
